import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Alert,
  Select,
  Option,
  FormControl,
  FormLabel,
  Checkbox,
  Table,
  LinearProgress,
  Chip,
} from '@mui/joy';
import { FileArrowUp as UploadFileIcon, Check as CheckIcon } from '@phosphor-icons/react';
import { priceListApi, distributorsApi, type Distributor } from '../../lib/distributorsApi';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  onComplete?: () => void;
}

export default function PriceListUpload({ onComplete }: Props) {
  const { showSuccess, showError } = useNotification();
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [selectedDistributor, setSelectedDistributor] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<any>(null);

  // Load distributors on mount
  React.useEffect(() => {
    loadDistributors();
  }, []);

  const loadDistributors = async () => {
    try {
      const response = await distributorsApi.list({ is_active: true });
      const distributorList = response?.results || [];
      setDistributors(distributorList);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load distributors');
      setDistributors([]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|xlsx?|csv)$/i)) {
        setError('Please select a PDF or Excel file');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      setPreview(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedDistributor) {
      setError('Please select both a file and a distributor');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await priceListApi.uploadFile(
        selectedFile,
        selectedDistributor,
        replaceExisting
      );

      if ((result as any).error) {
        setError((result as any).error);
      } else {
        setPreview(result as any);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!preview) return;

    setLoading(true);
    setError(null);

    try {
      await priceListApi.bulkImport(
        preview.distributor.id,
        preview.all_items || preview.preview,  // Use all_items if available, fallback to preview
        replaceExisting
      );
      
      // Show success notification
      const itemCount = preview.total_items || (preview.all_items || preview.preview).length;
      showSuccess(`Successfully imported ${itemCount} items for ${preview.distributor.name}`);
      
      // Reset form
      setPreview(null);
      setSelectedFile(null);
      setSelectedDistributor(null);
      setReplaceExisting(false);
      
      // Notify parent component to navigate to Distributors tab
      if (onComplete) {
        onComplete();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to import items';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, any> = {
      shingles: 'primary',
      underlayment: 'success',
      ventilation: 'warning',
      fasteners: 'neutral',
      misc: 'neutral',
    };
    return colors[category] || 'neutral';
  };

  return (
    <Card>
      <Box sx={{ p: 3 }}>
        <Typography level="h3" sx={{ mb: 3 }}>
          Upload Price List
        </Typography>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!preview ? (
          <Box>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Select Distributor</FormLabel>
              <Select
                value={selectedDistributor}
                onChange={(_, value) => setSelectedDistributor(value as number)}
                placeholder="Choose a distributor"
              >
                {distributors && distributors.map((dist) => (
                  <Option key={dist.id} value={dist.id}>
                    {dist.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <Button
                component="label"
                startDecorator={<UploadFileIcon />}
                variant="outlined"
                color="primary"
                fullWidth
              >
                {selectedFile ? selectedFile.name : 'Select Price List File'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                />
              </Button>
            </Box>

            <Checkbox
              label="Replace existing price list"
              checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)}
              sx={{ mb: 3 }}
            />

            <Button
              fullWidth
              onClick={handleUpload}
              disabled={!selectedFile || !selectedDistributor}
              loading={loading}
              startDecorator={<UploadFileIcon />}
            >
              Upload and Parse
            </Button>
          </Box>
        ) : (
          <Box>
            <Alert color="success" sx={{ mb: 3 }}>
              Successfully parsed {preview.total_items} items from the file
            </Alert>

            <Typography level="title-md" sx={{ mb: 2 }}>
              Preview (First 10 Items)
            </Typography>

            <Box sx={{ overflowX: 'auto', mb: 3 }}>
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Product ID</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {preview && preview.preview && preview.preview.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{item.product_name}</td>
                      <td>{item.product_id || '-'}</td>
                      <td>
                        <Chip size="sm" color={getCategoryColor(item.category)}>
                          {item.category}
                        </Chip>
                      </td>
                      <td>${item.unit_price}</td>
                      <td>{item.unit_of_measure}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>

            {preview.total_items > 10 && (
              <Alert color="neutral" sx={{ mb: 3 }}>
                ... and {preview.total_items - 10} more items
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setPreview(null)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmImport}
                loading={loading}
                startDecorator={<CheckIcon />}
              >
                Import {preview.total_items} Items
              </Button>
            </Box>
          </Box>
        )}

        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Box>
    </Card>
  );
}